import { type ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useCreateCatalogSupplier } from '@/shared/hooks/api/catalog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

type V2CreateSupplierFormValues = {
  name: string
  contactName: string
  contactEmail: string
}

type V2CreateSupplierDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: () => void
}

export function V2CreateSupplierDialog({
  open,
  onOpenChange,
  onCreated,
}: V2CreateSupplierDialogProps): ReactElement {
  const { t } = useTranslation('common')
  const { mutateAsync: createSupplier, isPending } = useCreateCatalogSupplier()

  const form = useForm<V2CreateSupplierFormValues>({
    defaultValues: {
      name: '',
      contactName: '',
      contactEmail: '',
    },
  })

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      form.reset()
    }
    onOpenChange(nextOpen)
  }

  function validateName(value: string | undefined): true | string {
    if (value == null || value.trim().length === 0) {
      return t('v2.pages.suppliers.create.validation.nameRequired')
    }
    return true
  }

  async function handleSubmit(values: V2CreateSupplierFormValues) {
    try {
      await createSupplier({
        name: values.name.trim(),
        contactName:
          values.contactName.trim().length > 0
            ? values.contactName.trim()
            : null,
        contactEmail:
          values.contactEmail.trim().length > 0
            ? values.contactEmail.trim()
            : null,
      })
      form.reset()
      onOpenChange(false)
      onCreated?.()
    } catch {
      toast.error(t('v2.pages.suppliers.create.errors.submitFailed'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('v2.pages.suppliers.create.title')}</DialogTitle>
          <DialogDescription>
            {t('v2.pages.suppliers.create.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              rules={{ validate: validateName }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('v2.pages.suppliers.create.nameLabel')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                      disabled={isPending}
                      placeholder={t(
                        'v2.pages.suppliers.create.namePlaceholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contactName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('v2.pages.suppliers.create.contactNameLabel')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                      disabled={isPending}
                      placeholder={t(
                        'v2.pages.suppliers.create.contactNamePlaceholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contactEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('v2.pages.suppliers.create.contactEmailLabel')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                      disabled={isPending}
                      placeholder={t(
                        'v2.pages.suppliers.create.contactEmailPlaceholder'
                      )}
                      type="email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={() => handleOpenChange(false)}
              >
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                )}
                {t('v2.pages.suppliers.create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
